/**
 * "My projects" on the Projects list (punch list F133, 30 Oct 2026): the
 * projects where the signed-in person is the PM or the TM.
 *
 * Matched on the Ajera KEY, never the display name -- two people can share a
 * name. A person not linked to Deltek (`me` null) has no projects of their
 * own, rather than every project with no PM.
 */
import type { ProjectView } from "@fieldpath/shared";

export function isMyProject(p: Pick<ProjectView, "pmKey" | "tmKey">, me: string | null): boolean {
  return me !== null && (p.pmKey === me || p.tmKey === me);
}

/**
 * Sorting the Projects list by a column (punch list F178, Jeremy 1 Nov 2026):
 * click a heading to sort by it, click again to reverse. Until a heading is
 * clicked the list keeps the server's order, newest first.
 *
 * Compared as the reader sees the cell -- names, not keys -- ignoring case,
 * and numbers in names in number order ("Phase 2" before "Phase 10"). A blank
 * cell sorts LAST either way, so reversing never puts a column of dashes on top.
 */
export const SORT_COLUMNS = ["name", "status", "type", "client", "pm", "tm", "ajera"] as const;
export type SortColumn = (typeof SORT_COLUMNS)[number];
export interface ProjectSort {
  readonly column: SortColumn;
  readonly descending: boolean;
}

/** The next sort after a heading is clicked: that column ascending, or the same column reversed. */
export function nextSort(current: ProjectSort | null, column: SortColumn): ProjectSort {
  return current?.column === column ? { column, descending: !current.descending } : { column, descending: false };
}

export function sortProjects<T>(
  list: readonly T[],
  sort: ProjectSort | null,
  cell: (row: T, column: SortColumn) => string | null,
): T[] {
  if (!sort) return [...list];
  const sign = sort.descending ? -1 : 1;
  return [...list].sort((a, b) => {
    const x = cell(a, sort.column)?.trim() || null;
    const y = cell(b, sort.column)?.trim() || null;
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    return sign * x.localeCompare(y, undefined, { sensitivity: "base", numeric: true });
  });
}
